/**
 * Development Log Service
 * 
 * Server-side logging service that:
 * 1. Forwards logs to external endpoint with authentication
 * 2. Stores logs in database for retrieval and analysis
 * 3. Feature-flagged for specific users (jp@naible.ai)
 */

import { blink } from '@/lib/blink'

export interface DevLogEntry {
  id?: string
  userId?: string
  userEmail?: string
  logType: 'json_parse_error' | 'api_error' | 'validation_error' | 'runtime_error' | 'custom'
  severity: 'debug' | 'info' | 'warning' | 'error' | 'critical'
  message: string
  context?: string
  stackTrace?: string
  inputData?: string
  metadata?: Record<string, any>
  source?: string
  endpoint?: string
  userAgent?: string
  ipAddress?: string
  sessionId?: string
  timestamp: string
}

export interface DevLogForwardConfig {
  endpoint?: string
  secret?: string
  enabled: boolean
}

/**
 * Get dev log configuration from environment/secrets
 */
function getDevLogConfig(): DevLogForwardConfig {
  // Check if dev logging is enabled
  const devEmail = localStorage?.getItem('devUserEmail') || sessionStorage?.getItem('userEmail')
  const enabled = devEmail === 'jp@naible.ai'

  return {
    endpoint: import.meta.env.VITE_DEV_LOG_ENDPOINT,
    secret: import.meta.env.VITE_DEV_LOG_SECRET,
    enabled
  }
}

/**
 * Forward log to external endpoint with authentication
 */
async function forwardLogToEndpoint(logEntry: DevLogEntry, config: DevLogForwardConfig): Promise<void> {
  if (!config.endpoint || !config.secret) {
    console.debug('[DevLog] No endpoint/secret configured - skipping forward')
    return
  }

  try {
    const response = await fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.secret}`,
        'X-Log-Source': 'naible-thread-ai'
      },
      body: JSON.stringify({
        ...logEntry,
        appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
        environment: import.meta.env.MODE || 'production'
      })
    })

    if (!response.ok) {
      console.error('[DevLog] Failed to forward log:', response.status, response.statusText)
    } else {
      console.debug('[DevLog] Successfully forwarded to external endpoint')
    }
  } catch (error) {
    console.error('[DevLog] Error forwarding log:', error)
  }
}

/**
 * Store log entry in database
 */
async function storeLogInDatabase(logEntry: DevLogEntry): Promise<string | null> {
  try {
    const logId = logEntry.id || `log_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
    
    const result = await blink.db.dev_logs.create({
      id: logId,
      userId: logEntry.userId || null,
      userEmail: logEntry.userEmail || null,
      logType: logEntry.logType,
      severity: logEntry.severity,
      message: logEntry.message,
      context: logEntry.context || null,
      stackTrace: logEntry.stackTrace || null,
      inputData: logEntry.inputData || null,
      metadata: logEntry.metadata ? JSON.stringify(logEntry.metadata) : null,
      source: logEntry.source || null,
      endpoint: logEntry.endpoint || null,
      userAgent: logEntry.userAgent || navigator?.userAgent || null,
      ipAddress: logEntry.ipAddress || null,
      sessionId: logEntry.sessionId || null,
      timestamp: logEntry.timestamp,
      createdAt: new Date().toISOString()
    })

    console.debug('[DevLog] Stored in database:', logId)
    return logId
  } catch (error) {
    console.error('[DevLog] Failed to store in database:', error)
    return null
  }
}

/**
 * Main logging function - forwards and stores dev logs
 */
export async function logDevEvent(logEntry: Omit<DevLogEntry, 'timestamp'>): Promise<void> {
  const config = getDevLogConfig()

  if (!config.enabled) {
    // Only log to console if not enabled
    console.debug('[DevLog] Disabled - not logging for this user')
    return
  }

  const fullLogEntry: DevLogEntry = {
    ...logEntry,
    timestamp: new Date().toISOString(),
    userEmail: logEntry.userEmail || localStorage?.getItem('devUserEmail') || undefined,
    userAgent: logEntry.userAgent || navigator?.userAgent || undefined
  }

  // Log to console immediately
  console.log('[DevLog]', fullLogEntry)

  // Store in database (always)
  const logId = await storeLogInDatabase(fullLogEntry)

  // Forward to external endpoint (if configured)
  if (config.endpoint && config.secret) {
    await forwardLogToEndpoint({ ...fullLogEntry, id: logId || undefined }, config)
  }

  // Also store in sessionStorage for dev tools
  try {
    const storedLogs = sessionStorage?.getItem('devLogs') || '[]'
    const logs = storedLogs ? JSON.parse(storedLogs) : []
    logs.push(fullLogEntry)
    sessionStorage?.setItem('devLogs', JSON.stringify(logs.slice(-100)))
  } catch (e) {
    // Silently fail storage - log parsing may fail with corrupted data
    console.debug('[DevLog] Failed to store in sessionStorage:', e)
  }
}

/**
 * Retrieve dev logs from database
 */
export async function getDevLogs(options: {
  userEmail?: string
  logType?: string
  severity?: string
  limit?: number
  offset?: number
}): Promise<DevLogEntry[]> {
  try {
    const whereClause: Record<string, any> = {}
    
    if (options.userEmail) {
      whereClause.userEmail = options.userEmail
    }
    if (options.logType) {
      whereClause.logType = options.logType
    }
    if (options.severity) {
      whereClause.severity = options.severity
    }

    const logs = await blink.db.dev_logs.list({
      where: Object.keys(whereClause).length > 0 ? whereClause : undefined,
      orderBy: { timestamp: 'desc' },
      limit: options.limit || 100
    })

    return logs.map((log: any) => {
      let parsedMetadata: Record<string, any> | undefined
      try {
        parsedMetadata = log.metadata ? JSON.parse(log.metadata) : undefined
      } catch (e) {
        console.debug('[DevLog] Failed to parse metadata for log:', log.id, e)
        parsedMetadata = undefined
      }

      return {
        id: log.id,
        userId: log.userId,
        userEmail: log.userEmail,
        logType: log.logType,
        severity: log.severity,
        message: log.message,
        context: log.context,
        stackTrace: log.stackTrace,
        inputData: log.inputData,
        metadata: parsedMetadata,
        source: log.source,
        endpoint: log.endpoint,
        userAgent: log.userAgent,
        ipAddress: log.ipAddress,
        sessionId: log.sessionId,
        timestamp: log.timestamp
      }
    })
  } catch (error) {
    console.error('[DevLog] Failed to retrieve logs:', error)
    return []
  }
}

/**
 * Clear old dev logs (keep last N days)
 */
export async function clearOldDevLogs(daysToKeep: number = 7): Promise<number> {
  try {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep)
    const cutoffISO = cutoffDate.toISOString()

    // Note: Blink SDK doesn't support batch delete yet, so we'd need to implement this
    // For now, return 0 and implement when needed
    console.log('[DevLog] Clear old logs not yet implemented')
    return 0
  } catch (error) {
    console.error('[DevLog] Failed to clear old logs:', error)
    return 0
  }
}

/**
 * Get log statistics
 */
export async function getDevLogStats(userEmail?: string): Promise<{
  total: number
  byType: Record<string, number>
  bySeverity: Record<string, number>
  recent24h: number
}> {
  try {
    const logs = await getDevLogs({ userEmail, limit: 1000 })
    
    const stats = {
      total: logs.length,
      byType: {} as Record<string, number>,
      bySeverity: {} as Record<string, number>,
      recent24h: 0
    }

    const now = Date.now()
    const oneDayAgo = now - 24 * 60 * 60 * 1000

    logs.forEach(log => {
      // Count by type
      stats.byType[log.logType] = (stats.byType[log.logType] || 0) + 1
      
      // Count by severity
      stats.bySeverity[log.severity] = (stats.bySeverity[log.severity] || 0) + 1
      
      // Count recent
      if (new Date(log.timestamp).getTime() > oneDayAgo) {
        stats.recent24h++
      }
    })

    return stats
  } catch (error) {
    console.error('[DevLog] Failed to get stats:', error)
    return {
      total: 0,
      byType: {},
      bySeverity: {},
      recent24h: 0
    }
  }
}
